import { defineStore } from 'pinia';
import { getTarifinfos } from '../api/TarifinfosService.js';

export const useTarifinfosStore = defineStore('tarifinfos', {
    state: () => ({
        // Raw Data vom Backend
        rawData: null,
        
        // Parsed Tarifinfos
        id: null,
        gueltigAbISO: null,
        gueltigBisISO: null,
        gueltigkeitVon: null,
        gueltigkeitBis: null,
        varianten: [],
        aktiv: false,
        
        // Meta-Informationen
        angelegtAm: null,
        angelegtVon: null,
        geaendertAm: null,
        geaendertVon: null,
        
        // Status
        isLoaded: false,
        isLoading: false,
        error: null,
        httpStatusCode: null,
        lastFetchTime: null,
    }),
    
    getters: {
        /**
         * Gibt alle verfügbaren Varianten zurück
         */
        verfuegbareVarianten: (state) => {
            return state.varianten.filter(v => 
                v.tarife && v.tarife.some(t => t.verfuegbar)
            );
        },
        
        /**
         * Gibt eine Variante anhand des Codes zurück
         */
        getVarianteByCode: (state) => {
            return (variantenCode) => {
                return state.varianten.find(v => v.variantenCode === variantenCode);
            };
        },
        
        /**
         * Gibt einen Tarif anhand Variante und Tarifcode zurück
         */
        getTarif: (state) => {
            return (variantenCode, tarifCode) => {
                const variante = state.varianten.find(v => v.variantenCode === variantenCode);
                if (!variante) return null;
                return variante.tarife.find(t => t.tarifCode === tarifCode);
            };
        },
        
        /**
         * Gibt alle verfügbaren Tarife einer Variante zurück
         */
        getVerfuegbareTarife: (state) => {
            return (variantenCode) => {
                const variante = state.varianten.find(v => v.variantenCode === variantenCode);
                if (!variante) return [];
                return variante.tarife.filter(t => t.verfuegbar);
            };
        },
        
        /**
         * Berechnet den Beitrag für ein bestimmtes Alter
         */
        getBeitragFuerAlter: (state) => {
            return (variantenCode, tarifCode, alter) => {
                const tarif = state.varianten
                    .find(v => v.variantenCode === variantenCode)
                    ?.tarife.find(t => t.tarifCode === tarifCode);
                
                if (!tarif || !tarif.altersgruppen) return null;
                
                const altersgruppe = tarif.altersgruppen.find(ag => 
                    alter >= ag.alterVon && alter <= ag.alterBis
                );
                
                return altersgruppe ? altersgruppe.beitrag : null;
            };
        },
        
        /**
         * Gibt den initialen Preis eines Tarifs zurück
         * @param {string} variantenCode - Code der Variante (z.B. 'Kombi', '4YOU')
         * @param {string} tarifCode - Code des Tarifs (z.B. 'ZVS', 'ZVP')
         * @returns {number|null} Initialer Preis oder null falls nicht gefunden
         */
        getInitialerPreis: (state) => {
            return (variantenCode, tarifCode) => {
                const tarif = state.varianten
                    .find(v => v.variantenCode === variantenCode)
                    ?.tarife.find(t => t.tarifCode === tarifCode);
                
                return tarif?.initialerPreis ?? null;
            };
        },
        
        /**
         * Prüft ob ein Tarif verfügbar ist
         * @param {string} variantenCode - Code der Variante (z.B. 'Kombi', '4YOU')
         * @param {string} tarifCode - Code des Tarifs (z.B. 'ZVS', 'ZVP')
         * @returns {boolean} true wenn verfügbar, false sonst
         */
        isTarifVerfuegbar: (state) => {
            return (variantenCode, tarifCode) => {
                const tarif = state.varianten
                    .find(v => v.variantenCode === variantenCode)
                    ?.tarife.find(t => t.tarifCode === tarifCode);
                
                return tarif?.verfuegbar ?? false;
            };
        },
        
        /**
         * Prüft ob Tarifinformationen gültig und aktuell sind
         */
        isValid: (state) => {
            if (!state.isLoaded || !state.aktiv) return false;
            
            // Prüfe ob Daten älter als 1 Stunde sind
            if (state.lastFetchTime) {
                const oneHour = 60 * 60 * 1000;
                const now = new Date().getTime();
                return (now - state.lastFetchTime) < oneHour;
            }
            
            return true;
        },
        
        /**
         * Gibt eine Liste aller Tarifcodes zurück (über alle Varianten)
         */
        alleTarifcodes: (state) => {
            const codes = new Set();
            state.varianten.forEach(variante => {
                variante.tarife?.forEach(tarif => {
                    codes.add(tarif.tarifCode);
                });
            });
            return Array.from(codes);
        },
    },
    
    actions: {
        /**
         * Lädt die Tarifinformationen vom Backend
         * @param {boolean} force - Erzwingt das Neuladen auch wenn bereits geladen
         * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
         */
        async loadTarifinfos(force = false) {
            // Wenn bereits geladen und nicht forciert, nicht erneut laden
            if (this.isLoaded && !force && this.isValid) {
                return true;
            }
            
            // Wenn bereits am Laden, warte
            if (this.isLoading) {
                return false;
            }
            
            this.isLoading = true;
            this.error = null;
            this.httpStatusCode = null;
            
            try {
                const response = await getTarifinfos();
                
                // Speichere HTTP-Statuscode
                this.httpStatusCode = response.status;
                
                // Prüfe auf HTTP-Erfolg (200-299)
                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`HTTP-Fehler: ${response.status} ${response.statusText}`);
                }
                
                // Prüfe auf Erfolg in der Response-Struktur
                if (response.data.ok && response.data.returncode === 0) {
                    const data = response.data.data;
                    
                    // Speichere Raw Data
                    this.rawData = data;
                    
                    // Parse und speichere strukturierte Daten
                    this.id = data.id;
                    this.gueltigAbISO = data.gueltigAbISO;
                    this.gueltigBisISO = data.gueltigBisISO;
                    this.aktiv = data.aktiv;
                    this.angelegtAm = data.angelegtAm;
                    this.angelegtVon = data.angelegtVon;
                    this.geaendertAm = data.geaendertAm;
                    this.geaendertVon = data.geaendertVon;
                    
                    // Parse Tarifinfos
                    if (data.tarifinfos?.tarifinfos) {
                        const tarifinfos = data.tarifinfos.tarifinfos;
                        this.gueltigkeitVon = tarifinfos.gueltigkeitVon;
                        this.gueltigkeitBis = tarifinfos.gueltigkeitBis;
                        this.varianten = tarifinfos.varianten || [];
                    }
                    
                    this.isLoaded = true;
                    this.lastFetchTime = new Date().getTime();
                    
                    return true;
                } else {
                    throw new Error(response.data.nachricht || 'Fehler beim Laden der Tarifinformationen');
                }
            } catch (error) {
                console.error('Fehler beim Laden der Tarifinformationen:', error);
                this.error = error.message || 'Unbekannter Fehler';
                this.isLoaded = false;
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        
        /**
         * Setzt den Store zurück
         */
        resetStore() {
            this.rawData = null;
            this.id = null;
            this.gueltigAbISO = null;
            this.gueltigBisISO = null;
            this.gueltigkeitVon = null;
            this.gueltigkeitBis = null;
            this.varianten = [];
            this.aktiv = false;
            this.angelegtAm = null;
            this.angelegtVon = null;
            this.geaendertAm = null;
            this.geaendertVon = null;
            this.isLoaded = false;
            this.isLoading = false;
            this.error = null;
            this.httpStatusCode = null;
            this.lastFetchTime = null;
        },
        
        /**
         * Initialisiert den Store (wird beim ersten Zugriff aufgerufen)
         */
        async initialize() {
            if (!this.isLoaded) {
                await this.loadTarifinfos();
            }
        },
    },
    
    persist: {
        storage: sessionStorage,
        // Nur die wichtigen Daten persistieren, nicht Loading-States
        paths: ['rawData', 'id', 'gueltigAbISO', 'gueltigBisISO', 'gueltigkeitVon', 
                'gueltigkeitBis', 'varianten', 'aktiv', 'isLoaded', 'lastFetchTime']
    }
});

